import { createSelector } from 'reselect';
import { IAppState } from 'spw-redux/reducer';
import { isCalibrationState } from 'spw-components/floorplan/calibration-constants';
import {
  ISpace,
  BulkSelectionState,
  MeasurementState,
} from 'spw-redux/modules/floorplan/types';
import { CUSTOM_CATEGORY } from 'spw-constants';
import { isNil } from 'ramda';
import { selectFloorplanImageData } from 'spw-redux/modules/projects/selectors';
import { FLOORPLAN_UPLOAD_STATUS } from 'spw-components/floorplan/poll-floorplan-upload-status-hook';
import { SUGGESTED_CALIBRATION_LINE_STATUS } from 'spw-esa-projects/building-details/floorplan/poll-suggested-calibration-line-hook';

export const selectOriginalCalibration = (state: IAppState) =>
  state.floorplanReducer.originalCalibration;

export const selectCalibration = (state: IAppState) =>
  state.floorplanReducer.calibration;
export const selectSuggestedCalibration = (state: IAppState) =>
  state.floorplanReducer.suggestedCalibration;
export const selectIsDrawingCalibrationLine = (state: IAppState) =>
  state.floorplanReducer.isDrawingCalibrationLine;
export const selectPreviousMapBoundsAndZoom = (state: IAppState) =>
  state.floorplanReducer.previousMapBoundsAndZoom;
export const selectCurrentControlState = (state: IAppState) =>
  state.floorplanReducer.currentControlState;
export const selectOldControlState = (state: IAppState) =>
  state.floorplanReducer.oldControlState;
export const selectCurrentFloorplan = (state: IAppState) =>
  state.floorplanReducer.currentFloorplan;
export const selectCurrentFloorplanUpload = (state: IAppState) =>
  state.floorplanReducer.currentFloorplanUpload;
export const selectFloorplanRequestStatus = (state: IAppState) =>
  state.floorplanReducer.floorplanRequestStatus;

export const selectEditHistory = (state: IAppState) =>
  state.floorplanReducer.editHistory;
export const selectPreviewHasBeenEdited = createSelector(
  selectEditHistory,
  (editHistory) => editHistory?.commands?.length > 0
);

export const selectIsUploadingFloorplan = (state: IAppState) =>
  state.floorplanReducer.isUploadingFloorplan;
export const selectIsArchivingFloorplan = (state: IAppState) =>
  state.floorplanReducer.isArchivingFloorplan;
export const selectIsUploadingPDF = (state: IAppState) =>
  state.floorplanReducer.isUploadingPDF;
export const selectActiveShape = (state: IAppState) =>
  state.floorplanReducer.activeShape;
export const selectFloorUsfShapeToCopy = (state: IAppState) =>
  state.floorplanReducer.floorUsfShapeCopy?.shape;
export const selectFloorUsfShapeSourceFloor = (state: IAppState) =>
  state.floorplanReducer.floorUsfShapeCopy?.sourceFloor;
export const selectFloorUsfShapeSourceCalibration = (state: IAppState) =>
  state.floorplanReducer.floorUsfShapeCopy?.calibration;

export const selectOriginalShape = (state: IAppState) =>
  state.floorplanReducer.originalShape;

export const selectAllShapes = (state: IAppState) =>
  state.floorplanReducer.shapes;

export const selectSpacesMap = (state: IAppState) => {
  return state.floorplanReducer.spacesMap;
};

export const selectFloorSpaces = (state: IAppState) => {
  return state.floorplanReducer.floorSpaces;
};

export const selectFloorSpacesBySpaceType = createSelector(
  selectFloorSpaces,
  (floorSpaces) =>
    Object.entries(floorSpaces).reduce(
      (
        map: Record<string, Record<number, ISpace[]>>,
        [category, categorySpaces]
      ) => {
        if (category === CUSTOM_CATEGORY) return map;
        return {
          ...map,
          [category]: categorySpaces.reduce(
            (
              spacesBySpaceType: Record<number, ISpace[]>,
              space
            ): {
              [key: number]: ISpace[];
            } => {
              if (spacesBySpaceType[space.projectSpaceType.id]) {
                return {
                  ...spacesBySpaceType,
                  [space.projectSpaceType.id]: [
                    ...spacesBySpaceType[space.projectSpaceType.id],
                    space,
                  ],
                };
              }

              return {
                ...spacesBySpaceType,
                [space.projectSpaceType.id]: [space],
              };
            },
            {}
          ),
        };
      },
      {}
    )
);

export const selectIsLoadingSpaces = (state: IAppState) => {
  return state.floorplanReducer.isLoadingSpaces;
};

export const selectClusters = (state: IAppState) =>
  state.floorplanReducer.clusters;

export const selectNeedToBuildFloorplanLayers = (state: IAppState) =>
  state.floorplanReducer.needToBuildLayers;

export const selectSelectedSpace = (state: IAppState) =>
  state.floorplanReducer.selectedSpace;
export const selectSelectedSpaces = (state: IAppState) =>
  state.floorplanReducer.selectedSpaceLayers?.[0]?.spaces;
export const selectSelectedSpaceLayers = (state: IAppState) =>
  state.floorplanReducer.selectedSpaceLayers;
export const selectBulkOperationSourceFloorCalibration = (state: IAppState) =>
  state.floorplanReducer.bulkOperationSourceFloorCalibration;

export const selectSelectedSpacesMap = createSelector(
  selectSelectedSpaceLayers,
  (layers) =>
    layers?.[0]?.spaces?.reduce(
      (acc, s) => ({ ...acc, [s.id]: true }),
      {} as { [key: number]: boolean }
    ) || {}
);

export const selectToolbarMode = (state: IAppState) =>
  state.floorplanReducer.toolbarMode;

export const selectMeasurementState = (state: IAppState) =>
  state.floorplanReducer.measurementState;

export const selectBulkSelectionState = (state: IAppState) =>
  state.floorplanReducer.bulkSelectionState;

export const selectIsBulkCopyingOrMoving = createSelector(
  selectBulkSelectionState,
  (bulkSelectionState) => {
    return [BulkSelectionState.COPY, BulkSelectionState.MOVE].includes(
      bulkSelectionState
    );
  }
);

export const selectFloorMapState = (state: IAppState) =>
  state.floorplanReducer.floorMapState;

export const selectPreviewState = (state: IAppState) =>
  state.floorplanReducer.previewState;
export const selectFloorplanPreviewImageBounds = (state: IAppState) =>
  state.floorplanReducer.previewImageBounds;

export const selectMiniModalState = (state: IAppState) =>
  state.floorplanReducer.miniModalState;

export const selectAddNoteDialogState = (state: IAppState) =>
  state.floorplanReducer.addNoteDialogState;

export const selectAddSpaceDialogState = (state: IAppState) =>
  state.floorplanReducer.addSpaceDialogState;

export const selectSelectionShapePoints = (state: IAppState) =>
  state.floorplanReducer.selectionShapePoints;

export const selectFloorplanGeoJson = (state: IAppState) =>
  state.floorplanReducer.floorplanGeoJson;

export const selectOriginalFileDataUrl = (state: IAppState) =>
  state.floorplanReducer.originalFileDataUrl;

export const selectOriginalImageDimensions = (state: IAppState) =>
  state.floorplanReducer.originalImageDimensions;
export const selectIsExitFloorplanOperationDialogOpen = (state: IAppState) =>
  state.floorplanReducer.isExitFloorplanOperationDialogOpen;
export const selectShowCancelUploadDialog = (state: IAppState) =>
  state.floorplanReducer.showCancelUploadDialog;

export const selectTooltipPositionState = (state: IAppState) =>
  state.floorplanReducer.tooltipPositionState;

export const selectMapMounted = (state: IAppState) =>
  state.floorplanReducer.mapMounted;

export const selectIsMeasuredShapeDirty = (state: IAppState) =>
  state.floorplanReducer.isMeasuredShapeDirty;

export const selectIsMeasuringUSF = createSelector(
  selectMeasurementState,
  (measurementState) => {
    return [
      MeasurementState.CREATE_SPACE_USF,
      MeasurementState.CREATE_FLOOR_USF,
      MeasurementState.EDIT_SPACE_USF,
      MeasurementState.EDIT_FLOOR_USF,
    ].includes(measurementState);
  }
);

export const selectIsCalibratingFloor = createSelector(
  selectMeasurementState,
  (measurementState) => {
    return isCalibrationState(measurementState);
  }
);

export const selectIsMeasuringFloorUSF = createSelector(
  selectMeasurementState,
  (measurementState) =>
    [
      MeasurementState.EDIT_FLOOR_USF,
      MeasurementState.CREATE_FLOOR_USF,
    ].includes(measurementState)
);

export const selectBulkOpIsInProgress = createSelector(
  selectBulkSelectionState,
  (bulkSelectionState) => !!bulkSelectionState
);

export const selectSpaceToUpdate = (state: IAppState) =>
  state.floorplanReducer.spaceToUpdate;

export const selectIsDownloadingWorksheetSpreadsheet = (state: IAppState) =>
  state.floorplanReducer.isDownloadingWorksheetSpreadsheet;

export const selectIsDownloadingFloorplanPDF = (state: IAppState) =>
  state.floorplanReducer.isDownloadingFloorplanPDF;

export const selectFloorNameWhereObjectsComeFrom = (state: IAppState) =>
  state.floorplanReducer.floorNameWhereObjectsComeFrom;

export const selectAdjustedFloorplanItems = (state: IAppState) =>
  state.floorplanReducer.adjustedFloorplanItems;

export const selectChangesForBulkOperationArePending = (state: IAppState) =>
  state.floorplanReducer.changesForBulkOperationArePending;

export const selectIsFloorplanReadyForUpload = createSelector(
  selectCurrentFloorplan,
  selectFloorplanImageData,
  (currentFloorplan, imageData) =>
    isNil(currentFloorplan?.s3Path) && !isNil(imageData)
);

export const selectSnappingLayerCreationComplete = createSelector(
  selectCurrentFloorplanUpload,
  (currentFloorplanUpload) => {
    return [
      FLOORPLAN_UPLOAD_STATUS.DONE,
      FLOORPLAN_UPLOAD_STATUS.FAILED,
    ].includes(currentFloorplanUpload?.geojsonStatus);
  }
);

export const selectIsSuggestedCalibrationProcessDone = createSelector(
  selectSuggestedCalibration,
  (suggestedCalibration) => {
    return (
      isNil(suggestedCalibration) ||
      [
        SUGGESTED_CALIBRATION_LINE_STATUS.DONE,
        SUGGESTED_CALIBRATION_LINE_STATUS.FAILED,
      ].includes(suggestedCalibration?.status)
    );
  }
);
